import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { MODAL_UNLOCKS } from '@/ui/screens/UnlockModal';
import { useServices } from '@/ui/services/ServicesContext';
import { Icon } from './Icon';
import styles from './ToastHost.module.css';

interface Toast {
  id: number;
  text: string;
  tone: 'gold' | 'soft';
}

const SHOW_MS = 2800;
const MAX_QUEUE = 4;
const SALE_TOAST_GAP_MS = 30_000;

/**
 * Gentle notifications (ux-screens §8): one visible at a time, queued, never during a drag or mini game
 * (those components simply aren't interrupted — toasts sit above the HUD and ignore pointers).
 */
/** `ready`: the room is on screen (toasts raised during the loading screen would never be seen). */
export function ToastHost({ ready }: { ready: boolean }) {
  const { t } = useI18n();
  const { bus, content, store, loadKind } = useServices();
  const [current, setCurrent] = useState<Toast | null>(null);
  const queue = useRef<Toast[]>([]);
  const seq = useRef(0);
  const loreShown = useRef(new Set<string>());
  const recoveredShown = useRef(false);
  const lastSaleToast = useRef(-Infinity);
  const showing = useRef(false);
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  });

  useEffect(() => {
    const push = (text: string, tone: Toast['tone']) => {
      const toast = { id: ++seq.current, text, tone };
      // Side effects stay outside state updaters (StrictMode may run updaters twice).
      if (showing.current) queue.current = [...queue.current, toast].slice(-MAX_QUEUE);
      else {
        showing.current = true;
        setCurrent(toast);
      }
    };
    // The main save failed its checks and the newest good backup was loaded (content-schemas §11.3).
    if (ready && loadKind === 'recovered' && !recoveredShown.current) {
      recoveredShown.current = true;
      push(tRef.current('system.save_recovered'), 'soft');
    }
    return bus.onAny((e) => {
      const tr = tRef.current;
      if (e.type === 'FRIENDSHIP_LEVEL_REACHED') {
        const npc = content.npcs.get(e.npcId);
        if (npc) push(tr('ui.toast.friendship', { name: tr(npc.nameKey), level: e.level }), 'gold');
      } else if (e.type === 'MAIL_RECEIVED') push(tr('ui.toast.mail', { count: e.mailIds.length }), 'soft');
      else if (e.type === 'SALE_COMPLETED' && store.getState().lastSession.sceneId !== 'minimart') {
        // At Home the Tiệm keeps selling (M2-07): a gentle note now and then, never a stream.
        const now = performance.now();
        if (now - lastSaleToast.current >= SALE_TOAST_GAP_MS) {
          lastSaleToast.current = now;
          push(tr('ui.toast.shop_sale', { total: e.total }), 'soft');
        }
      } else if (
        e.type === 'UNLOCK_GRANTED' &&
        !MODAL_UNLOCKS.has(e.unlockId) &&
        e.unlockId !== 'unl_home' &&
        e.unlockId !== 'unl_dusting'
      ) {
        const def = content.unlocks.get(e.unlockId);
        if (def) push(tr('ui.toast.unlocked', { name: tr(def.nameKey) }), 'gold');
      } else if (e.type === 'ACHIEVEMENT_COMPLETED') {
        const def = content.achievements.find((a) => a.id === e.achievementId);
        if (def && store.getState().settings.achievementToasts)
          push(tr('ui.toast.achievement', { name: tr(def.nameKey) }), 'gold');
      } else if (e.type === 'ORDER_CREATED') {
        const npc = content.npcs.get(e.npcId);
        if (npc) push(tr('ui.orders.toast_new', { npc: tr(npc.nameKey) }), 'soft');
      } else if (e.type === 'SHOOT_REQUESTED') {
        const npc = content.npcs.get(e.npcId);
        if (npc) push(tr('ui.studio.toast_new', { npc: tr(npc.nameKey) }), 'soft');
      } else if (e.type === 'ORDER_FULFILLED') {
        const tpl = content.orderTemplates.get(e.templateId);
        if (tpl) push(tr(tpl.thanksKey), 'gold');
      } else if (e.type === 'TASK_COMPLETED' && e.kind === 'tutorial')
        // Granny's little cheer when a tutorial step is done (story-dialogue §11).
        push(tr(`tutorial.${e.taskId}.done`), 'soft');
      else if (e.type === 'COZINESS_LEVEL_REACHED')
        push(tr('ui.toast.coziness_level', { level: e.level }), 'gold');
      else if (e.type === 'SET_COMPLETED') {
        const set = content.sets.get(e.setId);
        if (set) push(tr('ui.toast.set_complete', { name: tr(set.nameKey) }), 'gold');
      } else if (
        e.type === 'ITEM_PLACED' &&
        e.rightSpot &&
        e.origin === 'player' &&
        !loreShown.current.has(e.itemId)
      ) {
        // "1 dòng lore hài" the first time an item finds its right spot (M05-02).
        const def = content.items.get(e.itemId);
        if (def) {
          loreShown.current.add(e.itemId);
          push(tr(def.loreKey), 'soft');
        }
      }
    });
  }, [bus, content, store, loadKind, ready]);

  useEffect(() => {
    if (!current) return;
    const timer = setTimeout(() => {
      const next = queue.current.shift() ?? null;
      showing.current = next !== null;
      setCurrent(next);
    }, SHOW_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [current]);

  if (!current) return null;
  return (
    <div className={styles.host} role="status" aria-live="polite">
      <div key={current.id} className={styles.toast} data-tone={current.tone}>
        {current.tone === 'gold' && <Icon name="sparkle" size={20} className={styles.sparkle} />}
        {current.text}
      </div>
    </div>
  );
}
